import React, { Suspense, lazy } from 'react';
import { ExternalLink, Info, Ticket as TicketIcon, Trophy, Wallet } from 'lucide-react';
import CountdownTimer from '../Countdown/CountdownTimer';
import Tooltip from '../UI/Tooltip';
import Spinner from '../UI/Spinner';
import { shortAddress } from '../../utils';
import metamaskLogo from '../../assets/utility/metamask.webp';
import { useT } from '../../i18n';

const Banner = lazy(() => import('../../Banner'));
const Logs = lazy(() => import('../../Logs'));

const InfoRow = ({ label, value, tooltip }) => (
  <div className="flex items-center justify-between gap-4 border-b border-white/5 py-3 last:border-0">
    <span className="text-[0.7rem] uppercase leading-snug tracking-[0.14em] text-white/50">
      {label}
    </span>
    {tooltip ? (
      <Tooltip message={tooltip} className="shrink-0" align="right">
        <span className="notranslate lb-num inline-flex items-center gap-1.5 whitespace-nowrap text-base font-bold text-white">
          {value}
          <Info className="h-3.5 w-3.5 shrink-0 text-white/40" aria-hidden="true" />
        </span>
      </Tooltip>
    ) : (
      <span className="notranslate lb-num shrink-0 whitespace-nowrap text-base font-bold text-white">
        {value}
      </span>
    )}
  </div>
);

/**
 * One draw (daily or weekly): jackpot, countdown, ticket progress and the
 * winner of the previous round, all in a single scannable card.
 */
/** The contracts report the zero address until a round actually has a winner. */
const hasWinner = (address) => Boolean(address) && !/^0x0+$/i.test(address);

const DrawCard = ({ draw, coin, tone, ticketArt }) => {
  const t = useT();
  const filled = draw.maxTickets
    ? Math.min((Number(draw.tickets) / Number(draw.maxTickets)) * 100, 100)
    : 0;
  const isGold = tone === 'gold';
  const winnerKnown = hasWinner(draw.winner);

  return (
    <article
      data-tone={isGold ? 'gold' : undefined}
      className={`lb-ring lb-hoverable relative flex flex-col overflow-hidden rounded-[32px] border border-transparent p-5 sm:p-7 ${isGold ? 'lb-frame ' : ''}${
        isGold
          ? 'bg-gradient-to-br from-[#1a1406] via-[#16121b] to-[#100b15]'
          : 'bg-gradient-to-br from-[#0e0b13] via-[#130f19] to-[#1f1729]'
      }`}
    >
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full blur-3xl ${
          isGold ? 'bg-amber-400/10' : 'bg-brand/10'
        }`}
      />

      <header className="relative flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="lb-eyebrow">{draw.title}</p>
          <p className="notranslate mt-1 text-sm font-semibold text-white/60">
            {t('lottery.drawNumber', { id: draw.drawId })}
          </p>
        </div>
        <span className={`lb-chip ${isGold ? 'lb-chip-gold' : 'lb-chip-live'}`}>
          <span className="lb-dot" aria-hidden="true" />
          {t('lottery.live')}
        </span>
      </header>

      {/* Jackpot */}
      <div className="relative mt-6">
        <p className="lb-label">{t('lottery.currentJackpot')}</p>
        <p className="notranslate lb-num mt-2 break-words font-display text-4xl leading-none sm:text-5xl">
          <span className={isGold ? 'lb-gold-text' : 'text-white'}>
            {draw.jackpotCrypto} {coin.symbol}
          </span>
        </p>
        <p className="notranslate lb-num mt-2 text-lg text-white/60">
          ≈ {draw.jackpotUsd} USD
        </p>
      </div>

      {/* Countdown */}
      <div className="relative mt-6 rounded-2xl border border-white/10 bg-black/40 px-4 py-3">
        <p className="lb-label text-center">{t('lottery.nextSpinIn')}</p>
        <div className="mt-1 flex justify-center">
          <CountdownTimer targetDate={draw.nextSpin} />
        </div>
      </div>

      {/* Ticket progress */}
      <div className="relative mt-6">
        <div className="flex items-end justify-between gap-2">
          <span className="lb-label">{t('lottery.ticketsInDraw')}</span>
          <span className="notranslate lb-num text-sm font-bold text-white">
            {draw.maxTickets ? `${draw.tickets} / ${draw.maxTickets}` : draw.tickets}
          </span>
        </div>
        {draw.maxTickets ? (
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/10">
            <div
              style={{ width: `${filled}%` }}
              className={`h-full rounded-full transition-[width] duration-700 ${
                isGold ? 'lb-fill-money' : 'lb-fill-brand'
              }`}
            />
          </div>
        ) : null}
        <p className="mt-2 text-xs text-white/50">{draw.minimumNote}</p>
      </div>

      {/* Odds */}
      <div className="relative mt-5 flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
        <Tooltip message={t('lottery.oddsTooltip')} align="left">
          <span className="lb-label inline-flex items-center gap-1.5">
            {t('lottery.oddsPerTicket')}
            <Info className="h-3 w-3 text-white/40" aria-hidden="true" />
          </span>
        </Tooltip>
        <span className="notranslate lb-num shrink-0 text-base font-bold text-white">
          {/* an empty draw has no odds yet (1 / 0) */}
          {Number.isFinite(parseFloat(draw.chance)) ? draw.chance : '—'}
        </span>
      </div>

      {/* Previous winner */}
      <footer className="relative mt-6 rounded-2xl border border-white/10 bg-black/40 p-4">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${
              isGold ? 'border-amber-400/40 bg-amber-400/10' : 'border-[#b87ff0]/30 bg-brand/10'
            }`}
          >
            <Trophy
              className={`h-4 w-4 ${isGold ? 'text-amber-300' : 'text-brand-soft'}`}
              aria-hidden="true"
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="lb-label">{t('lottery.latestWinner')}</p>
            {winnerKnown ? (
              <a
                href={draw.winnerUrl}
                target="_blank"
                rel="noreferrer"
                className="notranslate mt-0.5 inline-flex items-center gap-1.5 text-sm font-bold text-white transition hover:text-brand-soft"
              >
                {shortAddress(draw.winner)}
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            ) : (
              <p className="mt-0.5 text-sm font-semibold text-white/55">{t('lottery.noWinnerYet')}</p>
            )}
          </div>
          {ticketArt && winnerKnown && draw.winnerTicket ? (
            <div className="relative hidden shrink-0 sm:block">
              <img src={ticketArt} alt="" aria-hidden="true" className="h-14 w-auto opacity-90" />
            </div>
          ) : null}
        </div>
        {winnerKnown ? (
          <p className="mt-3 text-xs text-white/50">
            {t('lottery.winningTicket')}{' '}
            <span className="notranslate font-bold text-white/80">#{draw.winnerTicket || '-'}</span>
          </p>
        ) : null}
      </footer>
    </article>
  );
};

const LotteryView = ({
  coin,
  coinPrice,
  onAddToWallet,
  loading,
  daily,
  weekly,
  ticketPrice,
  ticketPriceTooltip,
  contractTickets,
  onBuyTickets,
  ticketArt,
  txs = [],
}) => {
  const t = useT();

  return (
    <>
      <div className="lb-page relative w-full px-2 py-8 sm:px-4 sm:py-10">
        <div className="relative mx-auto w-full max-w-screen-xl">
          <div className="lb-shell">
            {/* Coin header */}
            <div className="flex flex-col gap-4 border-b border-white/5 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/5">
                  <img src={coin.logo} alt="" aria-hidden="true" className="h-9 w-9" />
                </div>
                <div>
                  <p className="lb-eyebrow">{t('lottery.eyebrow')}</p>
                  <h1 className="notranslate lb-heading mt-1 text-2xl sm:text-3xl">{coin.label}</h1>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="rounded-2xl border border-white/10 bg-black/40 px-4 py-2">
                  <p className="lb-label">{t('lottery.coinPrice', { symbol: coin.symbol })}</p>
                  <p className="notranslate lb-num text-lg font-bold text-white">${coinPrice}</p>
                </div>
                <button onClick={onAddToWallet} className="lb-btn lb-btn-ghost lb-btn-sm">
                  <img src={metamaskLogo} alt="" aria-hidden="true" className="h-4 w-4" />
                  {t('lottery.addToWallet')}
                </button>
                <a
                  href={coin.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lb-btn lb-btn-ghost lb-btn-sm notranslate"
                >
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('lottery.website')}
                </a>
              </div>
            </div>

            <div className="px-4 py-8 sm:px-8 sm:py-10">
              <div className="text-center">
                <p className="lb-eyebrow">{t('lottery.currentJackpots')}</p>
                <h2 className="lb-heading mt-3 text-3xl sm:text-4xl">
                  {t('lottery.twoDraws')} <span className="lb-gradient-text">{t('lottery.oneLuckySpin')}</span>
                </h2>
                <p className="mx-auto mt-3 max-w-2xl text-sm text-white/60 sm:text-base">
                  {t('lottery.intro')}
                </p>
              </div>

              {loading ? (
                <div className="mt-10 grid gap-6 lg:grid-cols-2">
                  {[0, 1].map((i) => (
                    <div
                      key={i}
                      className="flex min-h-[420px] items-center justify-center rounded-[32px] border border-white/10 bg-black/40"
                    >
                      <Spinner size="lg" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-10 grid gap-6 lg:grid-cols-2">
                  <DrawCard draw={daily} coin={coin} tone="cyan" ticketArt={ticketArt} />
                  <DrawCard draw={weekly} coin={coin} tone="gold" ticketArt={ticketArt} />
                </div>
              )}

              {/* Round facts + CTA */}
              <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1.4fr),minmax(0,1fr)]">
                <div className="lb-card p-5 sm:p-7">
                  <h3 className="lb-heading text-xl sm:text-2xl">{t('lottery.roundDetails')}</h3>
                  <p className="mt-2 text-sm text-white/60">
                    {t('lottery.roundDetailsSub')}
                  </p>
                  <div className="mt-5 grid gap-x-8 sm:grid-cols-2">
                    <InfoRow
                      label={t('lottery.pricePerTicket')}
                      value={ticketPrice}
                      tooltip={ticketPriceTooltip}
                    />
                    <InfoRow
                      label={t('lottery.contractTickets')}
                      value={contractTickets}
                      tooltip={t('lottery.contractTicketsTooltip')}
                    />
                    <InfoRow label={t('lottery.dailyDrawId')} value={`#${daily.drawId}`} />
                    <InfoRow label={t('lottery.weeklyDrawId')} value={`#${weekly.drawId}`} />
                  </div>
                </div>

                <div className="lb-section flex flex-col justify-between p-5 sm:p-7">
                  <div>
                    <p className="lb-eyebrow">{t('lottery.readyToPlay')}</p>
                    <h3 className="lb-heading mt-3 text-2xl">
                      {t('lottery.grabYour')} <span className="lb-gradient-text">{t('lottery.ticketsAccent')}</span>
                    </h3>
                    <ul className="mt-4 space-y-2.5 text-sm text-white/75">
                      <li className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-light" />
                        {t('lottery.perkHolder')}
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-light" />
                        {t('lottery.perkBulk')}
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                        {t('lottery.bothPools')}
                      </li>
                    </ul>
                  </div>
                  <button
                    onClick={onBuyTickets}
                    className="lb-btn lb-btn-primary lb-btn-lg mt-6 w-full"
                  >
                    <TicketIcon className="h-4 w-4" aria-hidden="true" />
                    {t('lottery.buyTickets')}
                  </button>
                  <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-white/45">
                    <Wallet className="h-3 w-3" aria-hidden="true" />
                    {t('lottery.walletRequired')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative">
        <Suspense fallback={null}>
          <Banner />
          <Logs transactions={txs} />
        </Suspense>
      </div>
    </>
  );
};

export default LotteryView;
